import React, { useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { Select } from "antd";
import ErrorText from "../../components/errorText";
import TriggerSelectionPopup from "./TriggerSelectionPopup";
import { 
  User, 
  FileText, 
  Calendar,
  MessageCircle, 
  Mail, 
  Receipt,
  ScrollText,
  Eye,
  Tag,
  ClipboardList,
  ChevronDown,
  ListChecks,
  PhoneIncoming,
  PhoneOutgoing,
  PhoneMissed,
  PhoneCall,
  Edit
} from 'lucide-react';

// CSS Modules import
import styles from './triggerSection.module.css';
import './TriggerSelectionPopup.css'; // Keep this for popup styles
import logger from "../../utils/logger";

const TIME_BASED_EVENTS = ['task_due_date', 'task_completed'];
const ACTIVITY_BASED_EVENTS = ['call_duration', 'call_count'];

const TriggerSection = ({
  automationData,
  automationWhenTriggerData,
  isShowAutoEvent,
  isUpdateAuto,
  handleAutoFormChange,
  sourceByData,
  statusLabelData,
  viewPageData,
  viewFileData,
  taskData,
  activityListData,
  customActivityType,
  organizationLeadList,
  errors,
}: any) => {
  const [showTriggerPopup, setShowTriggerPopup] = useState(false);
  
  const StoreData = useSelector((state: any) => {
    return state?.rootReducers;
  });
  const { customSource } = StoreData?.user?.userPreferences;

  // Debug logging
  logger.log("TriggerSection props:", {
    automationData,
    isShowAutoEvent,
    statusLabelData
  });

  // Parse targetValues into string[] for the multi-select
  const selectedValues: string[] = useMemo(() => {
    const tv = automationData?.targetValues;
    if (!tv) return [];
    if (Array.isArray(tv)) return tv;
    if (typeof tv === 'string' && tv.length > 0) return tv.split(',');
    return [];
  }, [automationData?.targetValues]);

  // Build options list based on event type
  const targetOptions = useMemo(() => {
    const event = automationData?.event;
    if (!event) return [];

    const options: { label: string; value: string }[] = [];

    switch (event) {
      case 'create_lead': {
        const merged = [
          ...(customSource?.map((e: any) => ({ _id: e.value, name: e.name, isActive: true })) || []),
          ...(sourceByData || []),
        ].filter((item: any) => item && item.isActive);
        merged.forEach((item: any) => options.push({ label: item.name, value: item._id }));
        options.push({ label: 'Any', value: 'any' });
        break;
      }
      case 'status_change':
      case 'label_change':
        (statusLabelData || []).forEach((item: any) => options.push({ label: item.name, value: item.value }));
        options.push({ label: 'Any', value: 'any' });
        break;
      case 'view_page':
        (viewPageData || []).forEach((item: any) => options.push({ label: item.details?.title, value: item._id }));
        options.push({ label: 'Any', value: 'any' });
        break;
      case 'view_file':
        (viewFileData || []).forEach((item: any) => options.push({ label: item.details?.title, value: item._id }));
        options.push({ label: 'Any', value: 'any' });
        break;
      case 'create_task':
        (taskData || []).forEach((item: any) => options.push({ label: item.name, value: item.value }));
        options.push({ label: 'Any', value: 'any' });
        break;
      case 'edit_task':
        options.push({ label: 'Due date is updated', value: 'toBePerformAt' });
        options.push({ label: 'Task is completed', value: 'isCompleted' });
        options.push({ label: 'Any', value: 'any' });
        break;
      case 'create_activity': {
        const allActivities = [...(activityListData || []), ...(customActivityType || [])];
        allActivities.forEach((item: any) => options.push({ label: item.name, value: item.value }));
        options.push({ label: 'Any', value: 'any' });
        break;
      }
      case 'new_lead_added':
        (organizationLeadList || []).forEach((item: any) => options.push({ label: item.name, value: item._id }));
        break;
      case 'task_due_date':
      case 'task_completed':
        (taskData || []).forEach((item: any) => options.push({ label: item.name, value: item.value }));
        options.push({ label: 'Any Task Type', value: 'any' });
        break;
    }

    return options;
  }, [automationData?.event, customSource, sourceByData, statusLabelData, viewPageData, viewFileData, taskData, activityListData, customActivityType, organizationLeadList]);

  // Handle multi-select value changes
  const handleTargetValuesChange = (values: string[]) => {
    // If "any" was just selected, expand to all non-any option values
    const prevHadAny = selectedValues.includes('any');
    const nowHasAny = values.includes('any');

    let finalValues = values;
    if (nowHasAny && !prevHadAny) {
      // User just clicked "Any" — select all specific options
      finalValues = targetOptions
        .filter(opt => opt.value !== 'any')
        .map(opt => opt.value);
    } else if (prevHadAny && !nowHasAny) {
      // User deselected something after "Any" was active — keep remaining
      finalValues = values.filter(v => v !== 'any');
    } else {
      // Normal selection — remove "any" if individual items are being picked
      finalValues = values.filter(v => v !== 'any');
    }

    logger.log('Target values changed (multi):', finalValues);

    // Pass array directly so backend receives ["lost","later"] not ["lost,later"]
    const syntheticEvent = {
      target: { name: 'targetValues', value: finalValues },
      currentTarget: { name: 'targetValues', value: finalValues }
    };
    handleAutoFormChange(syntheticEvent);
  };

  // Get display name for the selected trigger
  const getTriggerDisplayName = () => {
    if (!automationWhenTriggerData || !Array.isArray(automationWhenTriggerData)) {
      return "Select trigger";
    }
    const selected = automationWhenTriggerData.find(
      (item: any) => item.value === automationData?.event
    );
    return selected ? selected.name : "Select trigger";
  };

  // Get source label based on event type
  const getSourceLabel = (event: string): string => {
    switch (event) {
      case "create_lead":
        return "Lead source is";
      case "status_change":
        return "Status is updated to";
      case "label_change":
        return "Label is updated to";
      case "view_page":
        return "Page is";
      case "view_file":
        return "File is";
      case "create_activity":
        return "Activity is";
      case "create_task":
        return "Task type is";
      case "new_lead_added":
        return "List name";
      case "task_due_date":
      case "task_completed":
        return "Task type is";
      default:
        return "";
    }
  };

  // Handle trigger selection
  const handleTriggerSelect = (value: string) => {
    // Create a proper synthetic event to fix destructuring error
    const syntheticEvent = {
      target: { name: 'event', value },
      currentTarget: { name: 'event', value }
    };
    
    logger.log('Trigger selected:', value);
    handleAutoFormChange(syntheticEvent);
    setShowTriggerPopup(false);
  };

  // Format trigger options for popup
  const formatTriggerOptions = () => {
    logger.log('🔍 DEBUG: automationWhenTriggerData:', automationWhenTriggerData);
    
    if (!automationWhenTriggerData || !Array.isArray(automationWhenTriggerData)) {
      logger.log('❌ No trigger data available');
      return [];
    }
    
    const formatted = automationWhenTriggerData.map((item: any) => ({
      value: item.value,
      name: item.name || getTriggerNameByValue(item.value),
      description: item.description || getTriggerDescriptionByValue(item.value),
      category: item.category || getTriggerCategoryByValue(item.value),
      icon: getIconForTrigger(item.value)
    }));
    
    logger.log('✅ Formatted trigger options:', formatted);
    logger.log('🕐 Time-based triggers found:', formatted.filter(t => ['task_due_date', 'task_completed'].includes(t.value)));
    
    return formatted;
  };

  return (
    <div className={`col-md-12 when_then trigger-section ${styles.triggerSectionContainer}`}>
      {/* Step indicator */}
      <span className={styles.autoDot}>
        <label className={styles.dotNo}>1</label>
      </span>
      
      {/* Section header */}
      <label className={styles.whenThenLabel}>When this happens...</label>
      <span className={styles.whenThenTrigger}>
        Choose the event that starts your automation
      </span>
      
      <div className={styles.mainColumn}>
        {/* Visual trigger selector button */}
        <div className={styles.autoSelect}>
          <div 
            className={`${styles.triggerSelectorButton} ${isUpdateAuto ? styles.disabled : ""}`}
            onClick={() => {
              logger.log('Trigger selector clicked, isUpdateAuto:', isUpdateAuto);
              if (!isUpdateAuto) {
                setShowTriggerPopup(true);
              }
            }}
          >
            <span className={styles.triggerSelectedText}>
              {getTriggerDisplayName()}
            </span>
            {!isUpdateAuto && (
              <span className={styles.triggerSelectorArrow}>
                <ChevronDown size={16} />
              </span>
            )}
          </div>
          
          {/* Hidden original select for form compatibility */}
          <select
            name="event"
            value={automationData?.event}
            className={styles.hiddenSelect}
            onChange={(e) => handleAutoFormChange(e)}
            style={{ display: 'none' }}
          >
            <option value="">Select trigger</option>
            {automationWhenTriggerData && Array.isArray(automationWhenTriggerData) ? (
              automationWhenTriggerData.map((data: any, i: number) => (
                <option value={data.value} key={i}>
                  {data.name}
                </option>
              ))
            ) : null}
          </select>
        </div>

        {/* Trigger selection popup */}
        <TriggerSelectionPopup
          isOpen={showTriggerPopup}
          onClose={() => {
            logger.log('Trigger popup closed');
            setShowTriggerPopup(false);
          }}
          onSelect={handleTriggerSelect}
          currentValue={automationData?.event}
          isEditMode={isUpdateAuto}
          triggerOptions={formatTriggerOptions()}
        />

        {/* Condition dropdown - only show for certain events */}
        {(isShowAutoEvent && !TIME_BASED_EVENTS.includes(automationData?.event) && !ACTIVITY_BASED_EVENTS.includes(automationData?.event)) && (
          <div className={styles.conditionGroup}>
            <div className={styles.conditionLabels}>
              <span className={styles.andLabel}>AND</span>
              <span className={styles.andSource}>
                {getSourceLabel(automationData?.event)}
              </span>
            </div>
            <Select
              mode="multiple"
              placeholder="Click to select one or more values..."
              value={selectedValues.length > 0 ? selectedValues : []}
              onChange={handleTargetValuesChange}
              disabled={isUpdateAuto}
              options={targetOptions}
              className={styles.multiSelect}
              popupClassName="trigger-multi-select-dropdown"
              style={{ width: '100%' }}
              maxTagCount={5}
              maxTagPlaceholder={(omittedValues) => `+${omittedValues.length} more`}
              allowClear
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
              }
              listHeight={280}
            />
          </div>
        )}
        
        {/* Error display */}
        {errors.event && (
          <div className={styles.errorText}>
            <ErrorText message={errors.event} />
          </div>
        )}
      </div>
    </div>
  );
};

// Helper functions to provide standardized names, descriptions, and categories
const getTriggerNameByValue = (value: string): string => {
  switch (value) {
    case 'create_lead': return 'New Lead Added';
    case 'edit_lead': return 'Lead Information Updated';
    case 'status_change': return 'Lead Status Changed';
    case 'label_change': return 'Lead Label Changed';
    case 'new_lead_added': return 'Lead Added to List';
    case 'create_quotation': return 'New Quotation Created';
    case 'edit_quotation': return 'Quotation Updated';
    case 'create_invoice': return 'New Invoice Created';
    case 'edit_invoice': return 'Invoice Updated';
    case 'create_order': return 'New Order Created';
    case 'edit_order': return 'Order Updated';
    case 'create_task': return 'New Task Created';
    case 'edit_task': return 'Task Updated/Completed';
    case 'create_activity': return 'New Activity Logged';
    case 'view_page': return 'Lead Views Page';
    case 'view_file': return 'Lead Views Document';
    case 'send_email': return 'Email Sent';
    case 'send_whatsapp': return 'WhatsApp Message Sent';
    case 'task_due_date': return 'Task Due Date';
    case 'task_completed': return 'Task Completed';
    case 'call_duration': return 'Call Duration';
    case 'call_count': return 'Call Count';
    default: return value.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  }
};

const getTriggerDescriptionByValue = (value: string): string => {
  switch (value) {
    case 'create_lead': return 'Triggers when a new lead is created in your CRM';
    case 'edit_lead': return 'Triggers when any lead information is changed';
    case 'status_change': return 'Triggers when a lead moves to a different status';
    case 'label_change': return 'Triggers when a lead\'s label is updated';
    case 'new_lead_added': return 'Triggers when a lead is added to a specific list';
    case 'create_quotation': return 'Triggers when you create a new quotation for a lead';
    case 'edit_quotation': return 'Triggers when a quotation is edited or status changes';
    case 'create_invoice': return 'Triggers when you create a new invoice for a lead';
    case 'edit_invoice': return 'Triggers when an invoice is edited or status changes';
    case 'create_order': return 'Triggers when you create a new order for a lead';
    case 'edit_order': return 'Triggers when an order is edited or status changes';
    case 'create_task': return 'Triggers when you create a new follow-up task';
    case 'edit_task': return 'Triggers when a task is updated or marked as complete';
    case 'create_activity': return 'Triggers when you log a new activity with a lead';
    case 'view_page': return 'Triggers when a lead views a specific page on your website';
    case 'view_file': return 'Triggers when a lead opens a file you\'ve shared';
    case 'send_email': return 'Triggers when an email is sent to a lead';
    case 'send_whatsapp': return 'Triggers when a WhatsApp message is sent to a lead';
    case 'task_due_date': return 'Triggers before or after a task\'s due date';
    case 'task_completed': return 'Triggers before or after a task is completed';
    case 'call_duration': return 'Triggers when call duration meets specific criteria (in seconds)';
    case 'call_count': return 'Triggers when total number of calls (incoming + outgoing) meets criteria';
    default: return 'Triggers when this event occurs';
  }
};

const getTriggerCategoryByValue = (value: string): string => {
  // Check time-based triggers first
  if (value === 'task_due_date' || value === 'task_completed') {
    return 'Time-Based Triggers';
  }
  else if (value.includes('lead') || value === 'status_change' || value === 'label_change' || value === 'new_lead_added') {
    return 'Lead Management';
  } else if (value.includes('quotation') || value.includes('invoice') || value.includes('order')) {
    return 'Sales & Quotes';
  } else if (value.includes('task') || value.includes('activity') || value.includes('view_') || value.includes('call') || value === 'call_duration' || value === 'call_count') {
    return 'Activities & Tasks';
  } else if (value.includes('email') || value.includes('whatsapp')) {
    return 'Communication';
  } else {
    return 'Other';
  }
};

// Helper function to get icon for trigger type
const getIconForTrigger = (triggerValue: string) => {
  switch (triggerValue) {
    case 'create_lead':
    case 'edit_lead':
    case 'new_lead_added':
      return <User size={22} />;
    case 'status_change':
      return <ListChecks size={22} />;
    case 'label_change':
      return <Tag size={22} />;
    case 'create_quotation':
    case 'edit_quotation':
      return <ScrollText size={22} />;
    case 'create_invoice':
    case 'edit_invoice':
      return <Receipt size={22} />;
    case 'create_order':
    case 'edit_order':
      return <ClipboardList size={22} />;
    case 'create_task':
    case 'task_due_date':
    case 'task_completed':
      return <Calendar size={22} />;
    case 'edit_task':
      return <Edit size={22} />;
    case 'create_activity':
      return <FileText size={22} />;
    case 'incoming_call':
      return <PhoneIncoming size={22} />;
    case 'outgoing_call':
      return <PhoneOutgoing size={22} />;
    case 'missed_call':
      return <PhoneMissed size={22} />;
    case 'call_duration':
    case 'call_count':
      return <PhoneCall size={22} />;
    case 'view_page':
    case 'view_file':
      return <Eye size={22} />;
    case 'send_email':
      return <Mail size={22} />;
    case 'send_whatsapp':
      return <MessageCircle size={22} />;
    default:
      return <FileText size={22} />;
  }
};

export default TriggerSection;